import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useUserDetail } from './hooks';
import { TableSkeleton } from './components/LoadingSkeleton';
import { ErrorState } from './components/EmptyState';
import { PlanBadge, RoleBadge, UserStatusBadge } from './components/UserStatusBadge';
import { OverviewTab } from './tabs/OverviewTab';
import { ActivityTab } from './tabs/ActivityTab';
import { PaymentsTab } from './tabs/PaymentsTab';
import { SecurityTab } from './tabs/SecurityTab';
import { InvitationsTab } from './tabs/InvitationsTab';
import { DevicesTab } from './tabs/DevicesTab';
import { CreditsTab } from './tabs/CreditsTab';

type TabId =
  | 'overview'
  | 'invitations'
  | 'credits'
  | 'payments'
  | 'activity'
  | 'security'
  | 'devices';
const TABS: { id: TabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'invitations', label: 'Invitations' },
  { id: 'credits', label: 'Credits' },
  { id: 'payments', label: 'Payments' },
  { id: 'activity', label: 'Activity' },
  { id: 'security', label: 'Security' },
  { id: 'devices', label: 'Devices' },
];

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: user, isLoading, isError, error, refetch } = useUserDetail(id);
  const [tab, setTab] = useState<TabId>('overview');

  if (isLoading) return <TableSkeleton rows={4} cols={3} />;
  if (isError || !user) {
    return <ErrorState message={(error as Error)?.message ?? 'User not found'} onRetry={() => void refetch()} />;
  }

  return (
    <div className="space-y-6">
      <Link to="/users" className="text-xs text-on-surface-variant hover:text-on-surface flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Back to users
      </Link>

      <div className="glass-panel rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4">
        {user.avatar ? (
          <img src={user.avatar} alt="" className="w-16 h-16 rounded-full object-cover" />
        ) : (
          <div className="w-16 h-16 rounded-full bg-electric-violet/30 flex items-center justify-center text-2xl font-bold">
            {(user.name?.[0] ?? user.email[0]).toUpperCase()}
          </div>
        )}
        <div className="flex-1">
          <h2 className="font-display text-2xl font-bold">{user.name}</h2>
          <p className="text-sm text-on-surface-variant">{user.email}</p>
          <div className="flex flex-wrap gap-2 mt-2">
            <UserStatusBadge status={user.status} />
            <RoleBadge role={user.role} />
            <PlanBadge plan={user.plan} />
            {user.isVerified && (
              <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/15 text-emerald-300">
                Verified
              </span>
            )}
          </div>
        </div>
        {user.suspensionReason && (
          <div className="text-xs text-amber-300 max-w-xs">
            <div className="uppercase font-bold tracking-widest mb-1">Suspended</div>
            {user.suspensionReason}
          </div>
        )}
      </div>

      <div className="flex gap-1 border-b border-border-subtle overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={
              'px-4 py-2 text-xs font-bold uppercase tracking-widest whitespace-nowrap ' +
              (tab === t.id
                ? 'text-electric-violet border-b-2 border-electric-violet'
                : 'text-on-surface-variant hover:text-on-surface')
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      <div>
        {tab === 'overview' && <OverviewTab user={user} />}
        {tab === 'invitations' && <InvitationsTab userId={user.id} />}
        {tab === 'credits' && <CreditsTab userId={user.id} />}
        {tab === 'payments' && <PaymentsTab userId={user.id} />}
        {tab === 'activity' && <ActivityTab userId={user.id} />}
        {tab === 'security' && <SecurityTab user={user} />}
        {tab === 'devices' && <DevicesTab userId={user.id} />}
      </div>
    </div>
  );
}
